import {
  Button,
  Card,
  DismissIcon,
  Flex,
  Modal,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import type { BloomPartnerScript } from '@jane/business-admin/types';
import { businessPaths } from '@jane/business-admin/util';
import { StoreSelectModal } from '@jane/shared-b2b/components';

import styles from './PremiumExternalScripts.module.css';
import { ModalPageWrapper } from './components/ModalPageWrapper';
import { PremiumAddExternalScriptModal } from './components/PremiumAddExternalScriptModal';
import { PremiumExternalScript } from './components/PremiumExternalScript';

export const PremiumExternalScripts = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isLoading } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const formMethods = useForm<{
    partner_scripts: BloomPartnerScript[];
  }>({
    defaultValues: {
      partner_scripts: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control: formMethods.control,
    name: 'partner_scripts',
  });

  const [scriptDraft, setScriptDraft] = useState<
    | {
        name: string;
        script: string;
        storeIds: number[];
      }
    | undefined
  >(undefined);

  const stores = useMemo(
    () => bloomMenuQuery.data?.stores ?? [],
    [bloomMenuQuery.data?.stores]
  );

  const [isScriptModalOpen, setIsScriptModalOpen] = useState<boolean>(false);
  const [isStoresModalOpen, setIsStoresModalOpen] = useState<boolean>(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isFormInitialized, setIsFormInitialized] = useState<boolean>(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (bloomMenuQuery.data?.partner_scripts && !isFormInitialized) {
      formMethods.reset({
        partner_scripts: bloomMenuQuery.data.partner_scripts,
      });
      setIsFormInitialized(true);
    }
  }, [bloomMenuQuery.data?.partner_scripts, isFormInitialized, formMethods]);

  const publish = useCallback(() => {
    const formData = formMethods.getValues();
    updateBloomMenu(
      {
        partner_scripts: formData.partner_scripts,
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          toast.add({
            label: 'Scripts were added successfully!',
            variant: 'success',
          });
          setIsFormInitialized(false);
          navigate(businessPaths.premium());
        },
      }
    );
  }, [toast, updateBloomMenu, navigate, formMethods]);

  const handleAddScript = useCallback(() => {
    setEditingIndex(null);
    setScriptDraft(undefined);
    setIsScriptModalOpen(true);
  }, []);

  const handleEditScript = useCallback(
    (index: number) => {
      const script = fields[index];
      setEditingIndex(index);
      setScriptDraft({
        name: script.name || '',
        script: script.script || '',
        storeIds: script.storeIds || [],
      });
      setIsScriptModalOpen(true);
    },
    [fields]
  );

  const handleSaveScript = useCallback(
    (data: { name: string; script: string; storeIds: number[] }) => {
      if (editingIndex !== null) {
        const existingScript = fields[editingIndex];
        const updatedScript = {
          ...existingScript,
          ...data,
        };
        update(editingIndex, updatedScript);
      } else {
        append(data);
      }
      setIsScriptModalOpen(false);
    },
    [editingIndex, append, update, fields]
  );

  const handleDeleteScript = useCallback((index: number) => {
    setDeletingIndex(index);
  }, []);

  const confirmDelete = useCallback(() => {
    if (deletingIndex !== null) {
      remove(deletingIndex);
      setDeletingIndex(null);
    }
  }, [deletingIndex, remove]);

  return (
    <>
      <ModalPageWrapper
        onSubmit={publish}
        formMethods={formMethods}
        isLoading={isLoading}
        title="External scripts"
      >
        <Flex mt={32} mx="auto" maxWidth={944}>
          <Card className={styles['container']} mb={32}>
            <Card.Content p={40}>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                gap={24}
                mb={24}
              >
                <Typography variant="header-bold">External scripts</Typography>
                <Button
                  label="Add script"
                  variant="secondary"
                  onClick={handleAddScript}
                />
              </Flex>
              <Typography>
                Use this area to add external scripts (e.g., analytics,
                third-party tools). Proceed with caution; we only validate the
                format of your script, not its behavior. Improper or unsupported
                scripts may break your menu page or affect performance.
              </Typography>
            </Card.Content>

            {fields.length > 0 && (
              <Card.Content p={0}>
                {fields.map((field, index) => (
                  <PremiumExternalScript
                    key={field.id}
                    script={field}
                    index={index}
                    stores={stores}
                    onEdit={handleEditScript}
                    onDelete={handleDeleteScript}
                  />
                ))}
              </Card.Content>
            )}
          </Card>
        </Flex>
      </ModalPageWrapper>

      <PremiumAddExternalScriptModal
        isOpen={isScriptModalOpen}
        isEditing={editingIndex !== null}
        initialValues={scriptDraft}
        onClose={() => setIsScriptModalOpen(false)}
        onOpenStoresModal={(currentValues) => {
          setScriptDraft(currentValues);
          setIsStoresModalOpen(true);
        }}
        onSubmit={handleSaveScript}
        stores={stores}
      />

      {isStoresModalOpen && (
        <StoreSelectModal
          closeModal={() => setIsStoresModalOpen(false)}
          isFetchingStores={false}
          isLoadingStores={false}
          onSubmit={(selectedIds) => {
            setScriptDraft((prev) => ({
              ...prev,
              name: prev?.name || '',
              script: prev?.script || '',
              storeIds: selectedIds,
            }));
            setIsStoresModalOpen(false);
          }}
          selectedStoreIds={scriptDraft?.storeIds?.map(String) || []}
          storesData={stores}
        />
      )}

      <Modal
        open={deletingIndex !== null}
        onRequestClose={() => setDeletingIndex(null)}
        variant="dialogue"
        overlayClose
      >
        <Button.Icon
          icon={<DismissIcon />}
          label="Dismiss"
          onClick={() => setDeletingIndex(null)}
          ml="auto"
          variant="minimal"
        />
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={24}
          mt={-20}
          mb={24}
          mx={24}
        >
          <Typography variant="header-bold">Are you sure?</Typography>
          <Typography>
            Removing this will interrupt your script's behavior.
          </Typography>
          <Button
            label="Yes, remove"
            variant="destructive"
            onClick={confirmDelete}
          />
        </Flex>
      </Modal>
    </>
  );
};
